import type { UserHierarchyData, UserHierarchyNode } from 'types/userHierarchy';

const TEAM_GROUP_EMAIL_PREFIX = '__team__:';
const OTHER_TEAM_KEY = '__other__';
const OTHER_TEAM_LABEL = 'Other';

type HierarchyIndex = {
  rootEmail: string;
  nodeByEmail: Map<string, UserHierarchyNode>;
  parentByEmail: Map<string, string | null>;
  subtreeContactCountByEmail: Map<string, number>;
  subtreeContactIdsByEmail: Map<string, string[]>;
  selectableEmails: Set<string>;
};

type PipelineNodeRenderState = {
  checked: boolean;
  canToggle: boolean;
  contactCount: number;
  isManager: boolean;
};

type SalesRepDisplayMode = 'hierarchy' | 'teams';

function uniqueContactIds(contactIds: Iterable<string>): string[] {
  const unique = new Set<string>();
  for (const id of contactIds) {
    if (id) {
      unique.add(id);
    }
  }
  return Array.from(unique);
}

function hasSfContactId(node: UserHierarchyNode): boolean {
  return Boolean(node.User.SFContactId?.trim());
}

function buildHierarchyIndex(hierarchy: UserHierarchyNode): HierarchyIndex {
  const nodeByEmail = new Map<string, UserHierarchyNode>();
  const parentByEmail = new Map<string, string | null>();
  const subtreeContactIdsByEmail = new Map<string, string[]>();
  const subtreeContactCountByEmail = new Map<string, number>();
  const selectableEmails = new Set<string>();

  function walk(node: UserHierarchyNode, parentEmail: string | null) {
    const email = node.User.Email;
    nodeByEmail.set(email, node);
    parentByEmail.set(email, parentEmail);

    if (node.Reports?.length > 0) {
      selectableEmails.add(email);
    }

    const ids: string[] = [];
    if (node.User.SFContactId) {
      ids.push(node.User.SFContactId);
    }
    for (const report of node.Reports || []) {
      walk(report, email);
      const childIds = subtreeContactIdsByEmail.get(report.User.Email) ?? [];
      ids.push(...childIds);
    }
    const uniqueIds = uniqueContactIds(ids);
    subtreeContactIdsByEmail.set(email, uniqueIds);
    subtreeContactCountByEmail.set(email, uniqueIds.length);
  }

  walk(hierarchy, null);

  return {
    rootEmail: hierarchy.User.Email,
    nodeByEmail,
    parentByEmail,
    subtreeContactCountByEmail,
    subtreeContactIdsByEmail,
    selectableEmails,
  };
}

function filterHierarchyReportsByContactId(
  reports: UserHierarchyNode[],
): UserHierarchyNode[] {
  const visible: UserHierarchyNode[] = [];

  for (const report of reports) {
    const filteredChildren = filterHierarchyReportsByContactId(
      report.Reports || [],
    );

    if (hasSfContactId(report)) {
      visible.push({ ...report, Reports: filteredChildren });
    } else {
      visible.push(...filteredChildren);
    }
  }

  return visible;
}

function filterHierarchyByContactId(
  hierarchy: UserHierarchyNode,
): UserHierarchyNode {
  return {
    ...hierarchy,
    Reports: filterHierarchyReportsByContactId(hierarchy.Reports || []),
  };
}

function hasDirectReports(node: UserHierarchyNode): boolean {
  return (node.Reports || []).length > 0;
}

function sortReportsForDisplay(
  reports: UserHierarchyNode[],
): UserHierarchyNode[] {
  return [...reports].sort((a, b) => {
    const aHasReports = hasDirectReports(a) ? 0 : 1;
    const bHasReports = hasDirectReports(b) ? 0 : 1;
    return aHasReports - bHasReports;
  });
}

function filterSelectableReports(
  reports: UserHierarchyNode[],
  index: HierarchyIndex,
): UserHierarchyNode[] {
  return sortReportsForDisplay(reports).filter((report) =>
    index.selectableEmails.has(report.User.Email),
  );
}

function nodeLabel(node: UserHierarchyNode): string {
  const team = node.User.Team?.trim();
  const name = node.User.FullName?.trim() || node.User.Email;
  return team ? `${team} — ${name}` : name;
}

function isManagerNode(node: UserHierarchyNode): boolean {
  return hasDirectReports(node);
}

function buildPipelineHierarchyIndex(
  hierarchy: UserHierarchyNode,
): HierarchyIndex {
  const baseIndex = buildHierarchyIndex(hierarchy);
  const pipelineSelectableEmails = new Set<string>();

  for (const [email, node] of baseIndex.nodeByEmail) {
    if (isManagerNode(node) || node.User.SFContactId) {
      pipelineSelectableEmails.add(email);
    }
  }

  return {
    ...baseIndex,
    selectableEmails: pipelineSelectableEmails,
  };
}

function getAllContactIdsInScope(hierarchy: UserHierarchyNode): string[] {
  const index = buildPipelineHierarchyIndex(hierarchy);
  return index.subtreeContactIdsByEmail.get(hierarchy.User.Email) ?? [];
}

function getNodeContactIds(
  node: UserHierarchyNode,
  index: HierarchyIndex,
): string[] {
  if (isManagerNode(node) || isTeamGroupNode(node)) {
    return index.subtreeContactIdsByEmail.get(node.User.Email) ?? [];
  }
  if (node.User.SFContactId) {
    return [node.User.SFContactId];
  }
  return [];
}

function getPickerContactIdsInScope(
  hierarchy: UserHierarchyNode,
  excludeEmail?: string | null,
): string[] {
  if (excludeEmail?.trim()) {
    return uniqueContactIds(
      flattenHierarchyUsers(hierarchy, excludeEmail)
        .map((node) => node.User.SFContactId)
        .filter((id): id is string => Boolean(id?.trim())),
    );
  }
  return getAllContactIdsInScope(hierarchy);
}

function areAllContactIdsSelected(
  allInScope: string[],
  selectedContactIds: Set<string>,
): boolean {
  return (
    allInScope.length > 0 &&
    allInScope.every((id) => selectedContactIds.has(id))
  );
}

function isNodeContactIdsSelected(
  nodeContactIds: string[],
  allInScope: string[],
  selectedContactIds: Set<string>,
): boolean {
  if (areAllContactIdsSelected(allInScope, selectedContactIds)) {
    return true;
  }
  if (nodeContactIds.length === 0) {
    return false;
  }
  return nodeContactIds.every((id) => selectedContactIds.has(id));
}

function toggleAllContactIds(
  allInScope: string[],
  selectedContactIds: Set<string>,
): Set<string> {
  if (areAllContactIdsSelected(allInScope, selectedContactIds)) {
    return new Set();
  }
  return new Set(allInScope);
}

function toggleNodeContactIds(
  node: UserHierarchyNode,
  index: HierarchyIndex,
  selectedContactIds: Set<string>,
): Set<string> {
  if (!index.selectableEmails.has(node.User.Email)) {
    return selectedContactIds;
  }

  const nodeContactIds = getNodeContactIds(node, index);
  if (nodeContactIds.length === 0) {
    return selectedContactIds;
  }

  const next = new Set(selectedContactIds);
  const allNodeIdsSelected = nodeContactIds.every((id) => next.has(id));

  if (allNodeIdsSelected) {
    for (const id of nodeContactIds) {
      next.delete(id);
    }
  } else {
    for (const id of nodeContactIds) {
      next.add(id);
    }
  }

  return next;
}

function getNodeRenderState(
  node: UserHierarchyNode,
  index: HierarchyIndex,
  selectedContactIds: Set<string>,
  allInScope: string[],
): PipelineNodeRenderState {
  const email = node.User.Email;
  const selectable = index.selectableEmails.has(email);
  const nodeContactIds = getNodeContactIds(node, index);
  const checked = isNodeContactIdsSelected(
    nodeContactIds,
    allInScope,
    selectedContactIds,
  );

  return {
    checked,
    canToggle: selectable,
    contactCount: index.subtreeContactCountByEmail.get(email) ?? 0,
    isManager: isManagerNode(node),
  };
}

function computePipelineNodeRenderStates(
  hierarchy: UserHierarchyNode,
  index: HierarchyIndex,
  selectedContactIds: Set<string>,
  allInScope: string[],
): Map<string, PipelineNodeRenderState> {
  const nodeRenderStates = new Map<string, PipelineNodeRenderState>();

  function walk(node: UserHierarchyNode) {
    nodeRenderStates.set(
      node.User.Email,
      getNodeRenderState(node, index, selectedContactIds, allInScope),
    );
    for (const report of node.Reports || []) {
      walk(report);
    }
  }

  walk(hierarchy);
  return nodeRenderStates;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function isTeamGroupNode(node: UserHierarchyNode): boolean {
  return node.User.Email.startsWith(TEAM_GROUP_EMAIL_PREFIX);
}

function teamGroupEmail(teamKey: string): string {
  return `${TEAM_GROUP_EMAIL_PREFIX}${teamKey}`;
}

function getTeamKey(user: UserHierarchyData): string {
  const team = user.Team?.trim();
  return team || OTHER_TEAM_KEY;
}

function getTeamLabel(teamKey: string): string {
  return teamKey === OTHER_TEAM_KEY ? OTHER_TEAM_LABEL : teamKey;
}

function flattenHierarchyUsers(
  hierarchy: UserHierarchyNode,
  excludeEmail: string,
): UserHierarchyNode[] {
  const seen = new Set<string>();
  const users: UserHierarchyNode[] = [];
  const excluded = normalizeEmail(excludeEmail);

  function walk(node: UserHierarchyNode) {
    const email = node.User.Email.trim();
    const normalizedEmail = normalizeEmail(email);

    if (
      normalizedEmail !== excluded &&
      hasSfContactId(node) &&
      !seen.has(normalizedEmail)
    ) {
      seen.add(normalizedEmail);
      users.push({
        User: { ...node.User },
        Reports: [],
      });
    }

    for (const report of node.Reports || []) {
      walk(report);
    }
  }

  walk(hierarchy);
  return users;
}

function buildTeamGroupNode(
  teamKey: string,
  members: UserHierarchyNode[],
): UserHierarchyNode {
  const label = getTeamLabel(teamKey);

  return {
    User: {
      Email: teamGroupEmail(teamKey),
      FullName: label,
      EmployeeId: '',
      Status: 'Active',
      Role: 'Team',
      Team: label,
      Region: null,
      Coast: null,
      Campaign: '',
      Depth: 0,
      SFContactId: null,
    },
    Reports: members,
  };
}

function buildTeamGroupedHierarchy(
  hierarchy: UserHierarchyNode,
  excludeEmail: string,
): UserHierarchyNode {
  const users = flattenHierarchyUsers(hierarchy, excludeEmail);
  const groups = new Map<string, UserHierarchyNode[]>();

  for (const user of users) {
    const teamKey = getTeamKey(user.User);
    const members = groups.get(teamKey) ?? [];
    members.push(user);
    groups.set(teamKey, members);
  }

  const sortedTeamKeys = [...groups.keys()].sort((left, right) => {
    if (left === OTHER_TEAM_KEY) {
      return 1;
    }
    if (right === OTHER_TEAM_KEY) {
      return -1;
    }
    return left.localeCompare(right);
  });

  return {
    ...hierarchy,
    Reports: sortedTeamKeys.map((teamKey) =>
      buildTeamGroupNode(teamKey, groups.get(teamKey) ?? []),
    ),
  };
}

export {
  OTHER_TEAM_KEY,
  OTHER_TEAM_LABEL,
  TEAM_GROUP_EMAIL_PREFIX,
  areAllContactIdsSelected,
  buildPipelineHierarchyIndex,
  buildTeamGroupedHierarchy,
  computePipelineNodeRenderStates,
  filterHierarchyByContactId,
  filterSelectableReports,
  flattenHierarchyUsers,
  getAllContactIdsInScope,
  getPickerContactIdsInScope,
  hasDirectReports,
  isTeamGroupNode,
  nodeLabel,
  sortReportsForDisplay,
  toggleAllContactIds,
  toggleNodeContactIds,
  type HierarchyIndex,
  type PipelineNodeRenderState,
  type SalesRepDisplayMode,
};
